import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useIsNonDentalOrganization } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { DropdownSelect, Tabs } from '@/shared/ui';
import { OptionType as Option } from '@/shared/ui/Select/Select.types';

import {
  REPORT_FILTER_FAMILY_KEYS,
  ReportFilterFamilyKey,
  STUDIES_FILTER_MODES,
  STUDY_TYPE_TO_LABEL,
  StudiesFilterMode,
  TRANSCRIPTOR_FILTER_VALUE,
  reportFilterFamilyMessages,
  studiesFilterModeMessages,
  studyTypeMessages,
} from '../config';
import {
  isReportFilterFamilyKey,
  normalizeReportFilterFamilies,
  parseReportTypesParam,
} from '../lib';

import styles from './StudiesDropdownFilter.module.scss';

type StudiesDropdownFilterProps = {
  onChange?: (
    studyTypes: string[],
    hasTranscriptor: boolean | undefined,
    reportTypes: ReportFilterFamilyKey[],
  ) => void;
};

const isStudyTypeValue = (value: string) =>
  value !== TRANSCRIPTOR_FILTER_VALUE && !isReportFilterFamilyKey(value);

export const StudiesDropdownFilter: FC<StudiesDropdownFilterProps> = ({
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const isNonDental = useIsNonDentalOrganization();
  const [searchParams, setSearchParams] = useSearchParams();

  const readAppliedFilters = () => ({
    studyTypes: searchParams.get('studyTypes')?.split(',') || [],
    hasTranscriptor: searchParams.get('hasTranscriptor') === 'true',
    reportTypes: parseReportTypesParam(searchParams.get('reportTypes')),
  });

  const applied = readAppliedFilters();

  const [mode, setMode] = useState<StudiesFilterMode>('studies');
  const [studyFilters, setStudyFilters] = useState<string[]>(
    applied.studyTypes,
  );
  const [hasTranscriptor, setHasTranscriptor] = useState<boolean>(
    applied.hasTranscriptor,
  );
  const [reportFilters, setReportFilters] = useState<ReportFilterFamilyKey[]>(
    applied.reportTypes,
  );

  const dentalStudyOptions = useMemo(
    (): Option[] => [
      {
        value: StudyType.StudyType_CBCT.toString(),
        label: formatMessage(studyTypeMessages.cbct),
      },
      {
        value: StudyType.StudyType_IntraoralXRay.toString(),
        label: formatMessage(studyTypeMessages.ioxRay),
      },
      {
        value: StudyType.StudyType_PanoramicXRay.toString(),
        label: formatMessage(studyTypeMessages.pano),
      },
      {
        value: StudyType.StudyType_IOS_Meshes.toString(),
        label: formatMessage(studyTypeMessages.intraoralScan),
      },
      {
        value: StudyType.StudyType_DentalPhoto.toString(),
        label: formatMessage(studyTypeMessages.dentalPhotos),
      },
    ],
    [formatMessage],
  );

  const transcriptorOption = useMemo(
    (): Option => ({
      value: TRANSCRIPTOR_FILTER_VALUE,
      label: formatMessage(studyTypeMessages.transcriptor),
    }),
    [formatMessage],
  );

  const studyOptions = useMemo(
    (): Option[] =>
      isNonDental
        ? [transcriptorOption]
        : [...dentalStudyOptions, transcriptorOption],
    [isNonDental, dentalStudyOptions, transcriptorOption],
  );

  const reportOptions: Option[] = REPORT_FILTER_FAMILY_KEYS.map((key) => ({
    value: key,
    label: formatMessage(reportFilterFamilyMessages[key]),
  }));

  // Non-dental organizations have no reports to filter by: the toggle is hidden
  // and the panel stays in Studies mode (Transcriptor only).
  const showModeToggle = !isNonDental;
  const activeMode: StudiesFilterMode = showModeToggle ? mode : 'studies';
  const options = activeMode === 'reports' ? reportOptions : studyOptions;

  // The list shows only the active mode, but the value carries every mode's
  // selection so DropdownSelect's "changed since open" and "empty => clear"
  // logic covers the whole panel.
  const combinedValue = useMemo(() => {
    const values = [...studyFilters];
    if (hasTranscriptor) {
      values.push(TRANSCRIPTOR_FILTER_VALUE);
    }
    return [...values, ...reportFilters];
  }, [studyFilters, hasTranscriptor, reportFilters]);

  const handleChange = (value: string | string[]) => {
    if (typeof value === 'string') return;

    // react-select reports only the options it renders, so the inactive mode's
    // selection is kept instead of being read from the incoming value.
    if (activeMode === 'reports') {
      setReportFilters(normalizeReportFilterFamilies(value));
      return;
    }

    setHasTranscriptor(value.includes(TRANSCRIPTOR_FILTER_VALUE));
    setStudyFilters(value.filter(isStudyTypeValue));
  };

  // The applied state always equals the URL, so closing without Apply restores
  // every mode from it (DropdownSelect alone can only restore the visible mode).
  const handleCancel = () => {
    const restored = readAppliedFilters();
    setStudyFilters(restored.studyTypes);
    setHasTranscriptor(restored.hasTranscriptor);
    setReportFilters(restored.reportTypes);
  };

  const handleApply = (value: string | string[]) => {
    if (typeof value === 'string') return;

    const transcriptorSelected = value.includes(TRANSCRIPTOR_FILTER_VALUE);
    const studyTypeValues = value.filter(isStudyTypeValue);
    const reportFamilies = normalizeReportFilterFamilies(value);

    const studyLabels = studyTypeValues.map(
      (studyType) => STUDY_TYPE_TO_LABEL[studyType] || studyType,
    );

    if (transcriptorSelected) {
      studyLabels.push(formatMessage(studyTypeMessages.transcriptor));
    }

    if (studyLabels.length > 0) {
      trackEvent({
        eventName: 'PatientsPageStudiesFilterApplyButtonClicked',
        properties: {
          filter: studyLabels.join(','),
        },
      });
    }

    if (reportFamilies.length > 0) {
      trackEvent({
        eventName: 'PatientsPageReportsFilterApplyButtonClicked',
        properties: {
          filter: reportFamilies.join(','),
        },
      });
    }

    if (onChange) {
      onChange(
        studyTypeValues,
        transcriptorSelected || undefined,
        reportFamilies,
      );
    }

    if (studyTypeValues.length > 0) {
      searchParams.set('studyTypes', studyTypeValues.join(','));
    } else {
      searchParams.delete('studyTypes');
    }

    if (transcriptorSelected) {
      searchParams.set('hasTranscriptor', 'true');
    } else {
      searchParams.delete('hasTranscriptor');
    }

    if (reportFamilies.length > 0) {
      searchParams.set('reportTypes', reportFamilies.join(','));
    } else {
      searchParams.delete('reportTypes');
    }

    setSearchParams(searchParams);
  };

  const handleClear = () => {
    setStudyFilters([]);
    setHasTranscriptor(false);
    setReportFilters([]);
    if (onChange) {
      onChange([], undefined, []);
    }
    searchParams.delete('studyTypes');
    searchParams.delete('hasTranscriptor');
    searchParams.delete('reportTypes');
    setSearchParams(searchParams);
  };

  const modeToggle = showModeToggle ? (
    <Tabs.Root
      value={activeMode}
      onValueChange={(value) => setMode(value as StudiesFilterMode)}
      className={styles.modeToggle}
    >
      <Tabs.List size="small" variant="pill">
        {STUDIES_FILTER_MODES.map((filterMode) => (
          <Tabs.Trigger
            key={filterMode}
            value={filterMode}
            size="small"
            variant="pill"
          >
            {formatMessage(studiesFilterModeMessages[filterMode])}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
    </Tabs.Root>
  ) : undefined;

  return (
    <DropdownSelect
      isMulti
      value={combinedValue}
      label={formatMessage(studiesFilterModeMessages.studies)}
      options={options}
      menuHeader={modeToggle}
      className={styles.container}
      stylesOptions={{ listMaxHeight: 206, hideSearch: true }}
      icon="filter"
      multiValuesCount={
        combinedValue.length > 0 ? combinedValue.length : undefined
      }
      onChange={handleChange}
      onApply={handleApply}
      onClear={handleClear}
      onCancel={handleCancel}
    />
  );
};
